import React, { useEffect, useMemo, useState } from "react";
import { Badge, Table } from "react-bootstrap";
import { API_BASE_URL } from "../../utils/constants";
import { readErrorMessage } from "../../utils/apiError";
import { authUrl } from "../../utils/auth";
import MatchBlanks from "./MatchBlanks";
import AcceptChanges from "./AcceptChanges";
import TemplateEditor from "./TemplateEditor";

// The Word format of each document a bank (or product) prints. Every format is required to create the
// bank / product; editing can replace them. Each upload is checked at once and its problems are shown in plain words.

let catalogRequest = null;
const loadCatalog = () => {
  catalogRequest =
    catalogRequest ||
    fetch(`${API_BASE_URL}/api/doc-templates/catalog`).then(async (res) => {
      if (!res.ok) {
        catalogRequest = null;
        throw new Error(await readErrorMessage(res));
      }
      return (await res.json()).documents;
    });
  return catalogRequest;
};

const KIND_HINT = {
  list: (key) => `Repeat: {{#${key}}} … {{/${key}}}`,
  condition: (key) => `Show only when true: {{#${key}}} … {{/${key}}}`,
  image: () => "A picture: in Edit, click a picture and choose this (or Insert signature)",
};

const VariableList = ({ doc }) => {
  const [copied, setCopied] = useState("");
  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(text);
  };
  const groups = [...new Set(doc.variables.map((v) => v.group))];

  const nameCell = (text) => (
    <code role="button" title="Click to copy" onClick={() => copy(text)} style={{ whiteSpace: "nowrap" }}>
      {text}
      {copied === text && <span className="text-success ms-1">copied</span>}
    </code>
  );

  return (
    <div className="p-2 bg-light border rounded">
      <div className="mb-2 text-muted">
        A fill-in is a name in double brackets that the system replaces with the case's details. Type them in the Word
        file exactly as shown (click one to copy it). This document must have:{" "}
        {doc.required.map((key) => `{{${key}}}`).join(", ")}.
      </div>
      <Table size="sm" bordered className="mb-0 bg-white" style={{ fontSize: "11px" }}>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group}>
              <tr className="table-light">
                <th colSpan={3}>{group}</th>
              </tr>
              {doc.variables
                .filter((v) => v.group === group)
                .map((v) => (
                  <React.Fragment key={v.key}>
                    <tr>
                      <td style={{ width: "34%" }}>
                        {v.kind === "image" ? <span className="text-muted">(picture)</span> : nameCell(v.kind === "text" ? `{{${v.key}}}` : `{{#${v.key}}}`)}
                      </td>
                      <td>{v.label}</td>
                      <td className="text-muted" style={{ width: "34%" }}>
                        {KIND_HINT[v.kind]?.(v.key) || v.example}
                      </td>
                    </tr>
                    {(v.fields || []).map((f) => (
                      <tr key={`${v.key}.${f.key}`}>
                        <td className="ps-4">{nameCell(f.kind === "text" ? `{{${f.key}}}` : `{{#${f.key}}}`)}</td>
                        <td>
                          {f.label} <span className="text-muted">(inside {`{{#${v.key}}}`})</span>
                        </td>
                        <td className="text-muted">{KIND_HINT[f.kind]?.(f.key) || f.example}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const Problems = ({ problems }) => (
  <ol className="mb-0 ps-3">
    {problems.map((p, i) => (
      <li key={i} className="mb-1">
        <div className="text-danger">{p.problem}</div>
        <div>
          <strong>How to fix:</strong> {p.fix}
        </div>
        {p.where && <div className="text-muted">Near: “{p.where}”</div>}
      </li>
    ))}
  </ol>
);

// Upload slot of the one Word file used for all cases (backend MAIN_VARIATION)
const MAIN_VARIATION = "main";

const fileLink = (upload, label) => (
  <a href={authUrl(`${API_BASE_URL}/api/doc-templates/uploads/${upload.id}/file`)} title="Download this Word file">
    {label}
  </a>
);

/**
 * level: "bank" | "product"; owner: { clientId, productId } of the saved bank / product (null when creating).
 * Each document needs one Word file per variation (e.g. appointment letter with / without disbursement).
 * onChange({ selection, blocking }): selection = slot key ("document.variation") → uploaded file id to save;
 * blocking = why the form can't be saved yet ("" when it can).
 */
const DocumentFormats = ({ level, owner, onChange }) => {
  const [documents, setDocuments] = useState([]);
  const [current, setCurrent] = useState({}); // formats in use (edit), by slot key
  const [uploads, setUploads] = useState({}); // new uploads on this form, by slot key
  const [busy, setBusy] = useState({});
  const [open, setOpen] = useState({}); // { [slotKey]: "problems" | "variables" }
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null); // { slot, uploadId }
  const isCreate = !owner;
  const ownerQuery = owner ? `clientId=${owner.clientId}${owner.productId ? `&productId=${owner.productId}` : ""}` : "";

  useEffect(() => {
    setLoadError("");
    loadCatalog()
      .then((docs) => setDocuments(docs.filter((d) => d.level === level)))
      .catch((e) => setLoadError(`Could not load the document list: ${e.message}`));
  }, [level]);

  useEffect(() => {
    setUploads({});
    setOpen({});
    setCurrent({});
    if (!ownerQuery) return;
    fetch(`${API_BASE_URL}/api/doc-templates?${ownerQuery}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(await readErrorMessage(res));
        const list = await res.json();
        setCurrent(Object.fromEntries(list.map((t) => [`${t.docType}.${t.variation}`, t])));
      })
      .catch((e) => setLoadError(`Could not load the current Word files: ${e.message}`));
  }, [ownerQuery]);

  // One row per Word file: document + variation
  const slots = useMemo(
    () =>
      documents.flatMap((doc) =>
        doc.variations.map((variation, index) => ({
          key: `${doc.key}.${variation.key}`,
          doc,
          variation,
          many: doc.variations.length > 1,
          first: index === 0,
          label: doc.variations.length > 1 ? `${doc.label} – ${variation.label}` : doc.label,
        })),
      ),
    [documents],
  );

  const status = useMemo(() => {
    const hasFile = (key) => Boolean(uploads[key] || (!isCreate && current[key]));
    // A document is ready with one file for all cases, or a file for every version (same rule as the backend)
    const isComplete = (doc) =>
      hasFile(`${doc.key}.${MAIN_VARIATION}`) ||
      (doc.variations.length > 1 &&
        doc.variations.filter((v) => v.key !== MAIN_VARIATION).every((v) => hasFile(`${doc.key}.${v.key}`)));
    const incomplete = documents.filter((doc) => !isComplete(doc));
    const missing = slots.filter((s) => !hasFile(s.key) && incomplete.includes(s.doc));
    const withProblems = slots.filter((s) => uploads[s.key]?.problems?.length);
    const pending = slots.filter((s) => busy[s.key]);
    let blocking = "";
    if (loadError) blocking = loadError;
    else if (!slots.length) blocking = "Loading the document list…";
    else if (pending.length) blocking = "Wait for the Word files to finish uploading.";
    else if (withProblems.length) blocking = `Fix the problems in: ${withProblems.map((s) => s.label).join(", ")}.`;
    else if (isCreate && incomplete.length)
      blocking = `Upload the Word file for: ${incomplete
        .map((doc) => (doc.variations.length > 1 ? `${doc.label} (one file for all cases, or one for each version)` : doc.label))
        .join(", ")}.`;
    const selection = Object.fromEntries(
      Object.entries(uploads)
        .filter(([, u]) => u.id && !u.problems?.length)
        .map(([key, u]) => [key, u.id]),
    );
    return { missing, blocking, selection, readyCount: documents.length - incomplete.length };
  }, [documents, slots, uploads, current, busy, isCreate, loadError]);

  useEffect(() => {
    onChange({ selection: status.selection, blocking: status.blocking });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.blocking, JSON.stringify(status.selection)]);

  const upload = async (slot, file) => {
    if (!file) return;
    setBusy((b) => ({ ...b, [slot.key]: true }));
    try {
      const form = new FormData();
      form.append("docType", slot.doc.key);
      form.append("variation", slot.variation.key);
      form.append("file", file);
      const res = await fetch(`${API_BASE_URL}/api/doc-templates/uploads`, { method: "POST", body: form });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      const result = await res.json();
      setUploads((u) => ({ ...u, [slot.key]: result }));
      setOpen((o) => ({ ...o, [slot.key]: result.problems.length ? "problems" : undefined }));
    } catch (e) {
      setUploads((u) => ({
        ...u,
        [slot.key]: { originalName: file.name, problems: [{ problem: e.message, fix: "Try uploading the file again." }] },
      }));
      setOpen((o) => ({ ...o, [slot.key]: "problems" }));
    } finally {
      setBusy((b) => ({ ...b, [slot.key]: false }));
    }
  };

  // Edit opens the on-screen editor; a format already in use is copied first (it becomes the next version on save)
  const openEditor = async (slot) => {
    let u = uploads[slot.key];
    if (!u?.id) {
      setBusy((b) => ({ ...b, [slot.key]: true }));
      try {
        const res = await fetch(`${API_BASE_URL}/api/doc-templates/uploads/${current[slot.key].id}/copy`, { method: "POST" });
        if (!res.ok) throw new Error(await readErrorMessage(res));
        u = await res.json();
        setUploads((all) => ({ ...all, [slot.key]: u }));
      } catch (e) {
        setLoadError(`Could not open the editor: ${e.message}`);
        return;
      } finally {
        setBusy((b) => ({ ...b, [slot.key]: false }));
      }
    }
    setEditing({ slot, uploadId: u.id });
  };

  const toggle = (key, panel) => setOpen((o) => ({ ...o, [key]: o[key] === panel ? undefined : panel }));

  const checkCell = (slot) => {
    const u = uploads[slot.key];
    if (busy[slot.key]) return <span className="text-muted">Checking…</span>;
    if (u?.problems?.length)
      return (
        <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => toggle(slot.key, "problems")}>
          {u.problems.length} problem{u.problems.length === 1 ? "" : "s"} {open[slot.key] === "problems" ? "▴" : "▾"}
        </button>
      );
    if (u) return <Badge bg="success">Ready</Badge>;
    if (current[slot.key]) return <Badge bg="secondary">In use</Badge>;
    if (!status.missing.includes(slot)) return <span className="text-muted">Not needed</span>;
    return <Badge bg={isCreate ? "danger" : "warning"}>{isCreate ? "Required" : "Not uploaded"}</Badge>;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-baseline">
        <h6 className="mb-1" style={{ fontSize: "13px" }}>
          Document formats (Word) {isCreate && <span className="text-danger">*</span>}
        </h6>
        <span className="text-muted">
          {status.readyCount} of {documents.length} documents ready
        </span>
      </div>
      <p className="text-muted mb-2">
        Upload the bank's draft of each document as a Word (.docx) file. Some documents have more than one version (for
        example with / without disbursement): upload one file for all cases, or one file for each version — the system
        uses the right one for every case.
        After upload, match the bank's blanks (like <code>«Customer_Name»</code>) to what each case should print.
        {level === "product"
          ? " These documents differ by product (e.g. vehicle / education loan)."
          : " The Sec 17 order and the Award are uploaded on each product (Add / Edit Product), because they differ by product."}
      </p>
      {loadError && <div className="text-danger mb-2">{loadError}</div>}

      <Table size="sm" bordered className="align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th style={{ width: "28%" }}>Document</th>
            <th>Word file</th>
            <th style={{ width: "120px" }}>Check</th>
            <th style={{ width: "220px" }} className="text-center">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {slots.map((slot) => {
            const u = uploads[slot.key];
            const c = current[slot.key];
            return (
              <React.Fragment key={slot.key}>
                {slot.many && slot.first && (
                  <tr className="table-light">
                    <td colSpan={4} className="fw-semibold">
                      {slot.doc.label}
                      {isCreate && <span className="text-danger"> *</span>}{" "}
                      <span className="fw-normal text-muted">
                        — one Word file for all cases, or one for each of its {slot.doc.variations.length - 1} versions
                      </span>
                    </td>
                  </tr>
                )}
                <tr>
                  <td className={slot.many ? "ps-4" : "fw-semibold"}>
                    {slot.many ? slot.variation.label : slot.doc.label}
                    {isCreate && !slot.many && <span className="text-danger"> *</span>}
                  </td>
                  <td style={{ wordBreak: "break-word" }}>
                    {c && (
                      <div className={u ? "text-muted text-decoration-line-through" : ""}>
                        v{c.version} · {fileLink(c, c.originalName)}
                      </div>
                    )}
                    {u && <div>{u.id ? fileLink(u, u.originalName) : u.originalName}{c && " (new)"}</div>}
                    {!u && !c && <span className="text-muted">—</span>}
                  </td>
                  <td>{checkCell(slot)}</td>
                  <td>
                    <div className="d-flex justify-content-center gap-1">
                      <label
                        className={`btn btn-sm btn-outline-primary mb-0 ${busy[slot.key] ? "disabled" : ""}`}
                        style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                      >
                        {u || c ? "Replace" : "Upload"}
                        <input
                          type="file"
                          accept=".docx"
                          hidden
                          onChange={(e) => {
                            const file = e.target.files[0];
                            e.target.value = ""; // choosing the same (corrected) file again re-checks it
                            upload(slot, file);
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                        disabled={busy[slot.key] || !(u?.id || c)}
                        title="Open the file on screen: match blanks, fill-ins, text changes, and a real-case preview"
                        onClick={() => openEditor(slot)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                        onClick={() => toggle(slot.key, "variables")}
                      >
                        Fill-ins
                      </button>
                    </div>
                  </td>
                </tr>
                {open[slot.key] === "problems" && u?.problems?.length > 0 && (
                  <tr>
                    <td colSpan={4} style={{ background: "#fdecea" }}>
                      <AcceptChanges
                        upload={u}
                        onAccepted={(result) => {
                          setUploads((all) => ({ ...all, [slot.key]: result }));
                          if (!result.problems.length) setOpen((o) => ({ ...o, [slot.key]: undefined }));
                        }}
                      />
                      {u.blanks?.length > 0 && (
                        <MatchBlanks
                          upload={u}
                          doc={slot.doc}
                          onMatched={(result) => {
                            setUploads((all) => ({ ...all, [slot.key]: result }));
                            if (!result.problems.length) setOpen((o) => ({ ...o, [slot.key]: undefined }));
                          }}
                        />
                      )}
                      <div className="mb-1 fw-semibold text-danger">
                        {u.problems.length === 1 ? "What needs fixing:" : `${u.problems.length} things need fixing:`}
                      </div>
                      <Problems problems={u.problems} />
                      <div className="text-muted mt-1">
                        Anything not fixed here can be fixed with Edit, or in the Word file (then use Replace to upload it again).
                      </div>
                    </td>
                  </tr>
                )}
                {open[slot.key] === "variables" && (
                  <tr>
                    <td colSpan={4}>
                      <VariableList doc={slot.doc} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>

      {editing && (
        <TemplateEditor
          show
          uploadId={editing.uploadId}
          doc={editing.slot.doc}
          variation={editing.slot.variation}
          onClose={(latest) => {
            if (latest) {
              const { blocks, ...summary } = latest;
              setUploads((all) => ({ ...all, [editing.slot.key]: summary }));
              setOpen((o) => ({ ...o, [editing.slot.key]: summary.problems.length ? "problems" : undefined }));
            }
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

export default DocumentFormats;
